import { type Instant, type Money, fromApiMoney, parseInstant, toApiMoney } from '@taler/domain'
import type { Schemas } from '../contract/types.ts'
import { forgetOnFinalClientError } from '../http/idempotency.ts'
import type { TalerClient } from '../http/client.ts'
import { type CacheKey, type Invalidations, ledgerKey } from '../http/keys.ts'

export interface Contact {
  readonly id: string
  readonly name: string
  readonly notes: string | undefined
  /** The contact's one, currency-agnostic backing account - it can hold a genuine balance
   * in any currency at once, each tracked separately by `ContactsRepository.balances`. */
  readonly accountId: string
  readonly createdAt: Instant
  readonly updatedAt: Instant
}

export interface ForgiveContactInput {
  /** Omitted: forgives every currency owed, in full. Given: forgives only that one currency. */
  readonly amount?: Money
  /** Omitted: the ledger's `EXPENSE.GIFTS` system account. */
  readonly expenseAccountId?: string
}

export interface ContactForgiveResult {
  readonly entryId: string
  readonly amount: Money
}

export const contactKeys = {
  list: (ledgerId: string): CacheKey => ledgerKey(ledgerId, 'contacts', 'list'),
  detail: (ledgerId: string, contactId: string): CacheKey =>
    ledgerKey(ledgerId, 'contacts', 'detail', contactId),
  balances: (ledgerId: string, contactId: string): CacheKey =>
    ledgerKey(ledgerId, 'contacts', 'balances', contactId),
  /** Everything a write to the contact directory makes stale. */
  invalidatedByWrite: (ledgerId: string): Invalidations => [ledgerKey(ledgerId, 'contacts')],
} as const

export interface ContactsRepository {
  list(ledgerId: string, signal?: AbortSignal): Promise<readonly Contact[]>
  get(ledgerId: string, contactId: string, signal?: AbortSignal): Promise<Contact>
  create(ledgerId: string, input: Schemas['CreateContactRequest']): Promise<Contact>
  update(ledgerId: string, contactId: string, input: Schemas['UpdateContactRequest']): Promise<Contact>
  delete(ledgerId: string, contactId: string): Promise<void>
  /** One entry per currency the contact has touched; never sum these into one figure. */
  balances(ledgerId: string, contactId: string, signal?: AbortSignal): Promise<readonly Money[]>
  /** Final and irreversible - one result per currency written off. */
  forgive(
    ledgerId: string,
    contactId: string,
    input: ForgiveContactInput,
  ): Promise<readonly ContactForgiveResult[]>
}

export function createContactsRepository(client: TalerClient): ContactsRepository {
  const base = (ledgerId: string) => `/api/v1/ledgers/${encodeURIComponent(ledgerId)}/contacts`
  const one = (ledgerId: string, contactId: string) => `${base(ledgerId)}/${encodeURIComponent(contactId)}`

  return {
    async list(ledgerId, signal) {
      const contacts = await client.call<Schemas['ContactResponse'][]>({
        method: 'GET',
        path: base(ledgerId),
        ...(signal === undefined ? {} : { signal }),
      })
      return contacts.map(toContact)
    },

    async get(ledgerId, contactId, signal) {
      return toContact(
        await client.call<Schemas['ContactResponse']>({
          method: 'GET',
          path: one(ledgerId, contactId),
          ...(signal === undefined ? {} : { signal }),
        }),
      )
    },

    async create(ledgerId, input) {
      return toContact(
        await client.call<Schemas['ContactResponse'], Schemas['CreateContactRequest']>({
          method: 'POST',
          path: base(ledgerId),
          body: input,
        }),
      )
    },

    async update(ledgerId, contactId, input) {
      return toContact(
        await client.call<Schemas['ContactResponse'], Schemas['UpdateContactRequest']>({
          method: 'PATCH',
          path: one(ledgerId, contactId),
          body: input,
        }),
      )
    },

    async delete(ledgerId, contactId) {
      await client.call<undefined>({ method: 'DELETE', path: one(ledgerId, contactId) })
    },

    async balances(ledgerId, contactId, signal) {
      const balances = await client.call<Schemas['ApiMoney'][]>({
        method: 'GET',
        path: `${one(ledgerId, contactId)}/balances`,
        ...(signal === undefined ? {} : { signal }),
      })
      return balances.map(fromApiMoney)
    },

    async forgive(ledgerId, contactId, input) {
      const body: Schemas['ForgiveContactRequest'] = {
        ...(input.amount === undefined ? {} : { amount: toApiMoney(input.amount) as Schemas['ApiMoney'] }),
        ...(input.expenseAccountId === undefined ? {} : { expenseAccountId: input.expenseAccountId }),
      }
      const path = `${one(ledgerId, contactId)}/forgive`
      const payload = { method: 'POST' as const, path, query: null, body }
      let response: Schemas['ForgiveContactResponse'][]
      try {
        response = await client.call<Schemas['ForgiveContactResponse'][], Schemas['ForgiveContactRequest']>({
          method: 'POST',
          path,
          body,
        })
      } catch (error) {
        forgetOnFinalClientError(client.idempotencyKeys, payload, error)
        throw error
      }
      client.idempotencyKeys.forget(payload)
      return response.map((item) => ({ entryId: item.entryId, amount: fromApiMoney(item.amount) }))
    },
  }
}

function toContact(response: Schemas['ContactResponse']): Contact {
  return {
    id: response.id,
    name: response.name,
    notes: response.notes,
    accountId: response.accountId,
    createdAt: parseInstant(response.createdAt),
    updatedAt: parseInstant(response.updatedAt),
  }
}
